import { useEffect, useState } from "react";
import {
  alpha,
  Avatar,
  Box,
  Button,
  ButtonGroup,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Grid,
  Typography
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import moment from "moment";
import { getEnvironmentVariable } from "../../../global/environmentVariables";
import { useTranslation } from "react-i18next";

const SINGLE_GAME_SYSTEM = getEnvironmentVariable("SINGLE_GAME_SYSTEM");
const LABEL_GAMESYSTEM = getEnvironmentVariable("LABEL_GAMESYSTEM");

const useStyles = makeStyles((theme) => ({
  text: {
    fontSize: "0.85em",
    marginBottom: "unset",
    "& .highlighted": {
      fontWeight: "600"
    }
  },
  wrapper: {
    "& .highlighted": {
      backgroundColor:
        theme?.palette?.highlight?.main || theme?.palette?.warning?.main,
      color:
        theme?.palette?.highlight?.contrastText ||
        theme?.palette?.warning?.contrastText
    }
  },
  privateWrapper: {
    backgroundColor: theme?.palette?.error?.main
      ? alpha(theme?.palette?.error?.main, 0.15)
      : null,
    "& .highlighted": {
      backgroundColor:
        theme?.palette?.highlight?.main || theme?.palette?.warning?.main,
      color:
        theme?.palette?.highlight?.contrastText ||
        theme?.palette?.warning?.contrastText
    }
  }
}));

const getWinRate = (resultRecord) => {
  if (!resultRecord?.length) return 0;
  let wins = 0;
  for (let record of resultRecord) {
    wins += record / 2;
  }
  return Math.round((wins / resultRecord.length) * 10000) / 100;
};

const getResultPoints = (record) => {
  if (!record?.length) return 0;
  let resultPoints = 0;
  for (let r of record) {
    resultPoints += r;
  }
  return resultPoints;
};

const getFontSize = (text) => {
  if (text.length <= 2) {
    return "1.5rem"; // Larger font for short names
  } else if (text.length <= 4) {
    return "1rem"; // Medium font for medium names
  }
  return "0.8rem"; // Smaller font for longer names
};

function DynamicAvatar({ text, sx, ...rest }) {
  return (
    <Avatar
      sx={{
        fontSize: getFontSize(text.trim()),
        bgcolor: "primary.main",
        color: "primary.contrastText",
        ...sx
      }}
      {...rest}
    >
      {text.trim()}
    </Avatar>
  );
}

export default function ArmyListCardV2(props) {
  const { armyList } = props;

  const classes = useStyles();
  const [factionItems, setFactionItems] = useState([]);
  const { t } = useTranslation("translation", {
    keyPrefix: "components.ArmyListCardV2"
  });

  useEffect(() => {
    let _factionItems = [];
    if (armyList?.player?.faction?.name)
      _factionItems.push(armyList?.player?.faction?.name.trim());
    if (
      armyList?.player?.army?.name &&
      armyList?.player?.faction?.name !== armyList?.player?.army?.name
    )
      _factionItems.push(armyList?.player?.army?.name.trim());
    // if (armyList?.detachment) _factionItems.push(armyList?.detachment.trim());
    setFactionItems(_factionItems);
    // eslint-disable-next-line
  }, [armyList.id]);

  if (!armyList) return <></>;

  return (
    <Box sx={{ width: "100%", ml: 2, mr: 2, mt: 1, mb: 1 }}>
      <Card sx={{ width: "100%", border: 0.5, p: 1 }}>
        <CardHeader
          avatar={
            <DynamicAvatar
              sx={{
                bgcolor: "primary.main",
                color: "primary.contrastText"
              }}
              aria-label={t("placing")}
              text={
                armyList?.player?.metrics?.placing
                  ? `#${armyList?.player?.metrics?.placing} `
                  : t("notAvailable")
              }
            />
          }
          title={`${armyList?.player?.firstName || armyList?.user?.firstName || ""} ${
            armyList?.player?.lastName || armyList?.user?.lastName || ""
          }`.trim()}
          subheader={`${armyList?.event?.name} - ${moment(armyList?.event?.date).format("ll")}`}
        />

        <CardContent
          sx={{
            pt: 1,
            pb: 1,
            pl: 2,
            pr: 2,
            "& .MuiCardContent-root:last-child": {
              pb: 0
            }
          }}
        >
          {/* <ImagePreview armyList={armyList} imageCache={imageCache} /> */}
          <Grid container spacing={2}>
            <Grid item xs={12}>
              {armyList?.player?.metrics?.total_resultRecord?.length ||
              armyList?.player?.metrics?.resultRecord?.length ? (
                <>
                  <Typography gutterBottom className={classes.text}>
                    <span
                      style={{
                        fontWeight: "600",
                        fontSize: "0.75rem",
                        lineHeight: "1.66"
                      }}
                    >
                      {t("winRate")}
                    </span>{" "}
                    {getWinRate(
                      armyList?.player?.metrics?.total_resultRecord ||
                        armyList?.player?.metrics?.resultRecord
                    )}
                    %
                  </Typography>
                  <Typography gutterBottom className={classes.text}>
                    <span
                      style={{
                        fontWeight: "600",
                        fontSize: "0.75rem",
                        lineHeight: "1.66"
                      }}
                    >
                      {t("record")}
                    </span>{" "}
                    {(
                      armyList?.player?.metrics?.total_resultRecord ||
                      armyList?.player?.metrics?.resultRecord
                    )?.filter((el) => el === 2)?.length || 0}
                    {"-"}
                    {(
                      armyList?.player?.metrics?.total_resultRecord ||
                      armyList?.player?.metrics?.resultRecord
                    )?.filter((el) => el === 0)?.length || 0}
                    {"-"}
                    {(
                      armyList?.player?.metrics?.total_resultRecord ||
                      armyList?.player?.metrics?.resultRecord
                    )?.filter((el) => el === 1)?.length || 0}{" "}
                    {t("winLossDraw")}
                  </Typography>
                  <Typography gutterBottom className={classes.text}>
                    <span
                      style={{
                        fontWeight: "600",
                        fontSize: "0.75rem",
                        lineHeight: "1.66"
                      }}
                    >
                      {t("result")}
                    </span>{" "}
                    {getResultPoints(
                      armyList?.player?.metrics?.total_record ||
                        armyList?.player?.metrics?.record
                    )}{" "}
                    {t("points")} (
                    {(
                      armyList?.player?.metrics?.total_resultRecord ||
                      armyList?.player?.metrics?.resultRecord
                    )?.map((res, index) => {
                      return (
                        <>
                          {index > 0 && <span> | </span>}
                          <span
                            style={{
                              color:
                                res === 2 ? "green" : res === 1 ? "blue" : "red"
                            }}
                          >
                            {(armyList?.player?.metrics?.total_record ||
                              armyList?.player?.metrics?.record)[index] !==
                            undefined
                              ? (armyList?.player?.metrics?.total_record ||
                                  armyList?.player?.metrics?.record)[index]
                              : res}
                          </span>
                        </>
                      );
                    })}
                    )
                  </Typography>
                </>
              ) : (
                <></>
              )}

              {factionItems?.length ? (
                <Typography gutterBottom className={classes.text}>
                  <span
                    style={{
                      fontWeight: "600",
                      fontSize: "0.75rem",
                      lineHeight: "1.66"
                    }}
                  >
                    {t("faction")}
                  </span>{" "}
                  {factionItems.join(" - ")}
                </Typography>
              ) : (
                <></>
              )}

              {armyList?.detachment ? (
                <Typography gutterBottom className={classes.text}>
                  <span
                    style={{
                      fontWeight: "600",
                      fontSize: "0.75rem",
                      lineHeight: "1.66"
                    }}
                  >
                    {SINGLE_GAME_SYSTEM?.code === 4
                      ? t("battleFormation")
                      : t("detachment")}
                    :
                  </span>{" "}
                  {armyList?.detachment}
                </Typography>
              ) : (
                <></>
              )}

              {!SINGLE_GAME_SYSTEM && armyList.gameSystem?.name && (
                <Typography gutterBottom className={classes.text}>
                  <span
                    style={{
                      fontWeight: "600",
                      fontSize: "0.75rem",
                      lineHeight: "1.66"
                    }}
                  >
                    {LABEL_GAMESYSTEM}:
                  </span>{" "}
                  {armyList.gameSystem?.name}
                </Typography>
              )}

              {armyList?.deckFormat && (
                <Typography gutterBottom className={classes.text}>
                  <span
                    style={{
                      fontWeight: "600",
                      fontSize: "0.75rem",
                      lineHeight: "1.66"
                    }}
                  >
                    {t("format")}
                  </span>{" "}
                  {armyList?.deckFormat}
                </Typography>
              )}
              {armyList?.subFaction?.name && (
                <Typography gutterBottom className={classes.text}>
                  <span
                    style={{
                      fontWeight: "600",
                      fontSize: "0.75rem",
                      lineHeight: "1.66"
                    }}
                  >
                    {armyList?.gameSystem?.subFactionLabel || t("subFaction")}:
                  </span>{" "}
                  {armyList?.subFaction?.name}
                </Typography>
              )}
              {armyList?.character?.name && (
                <Typography gutterBottom className={classes.text}>
                  <span
                    style={{
                      fontWeight: "600",
                      fontSize: "0.75rem",
                      lineHeight: "1.66"
                    }}
                  >
                    {t("character")}
                  </span>{" "}
                  {armyList?.character?.name}
                </Typography>
              )}
            </Grid>
          </Grid>
        </CardContent>
        <CardActions
          sx={{
            pt: 1,
            pb: 1,
            pl: 2,
            pr: 2
          }}
        >
          <ButtonGroup fullWidth>
            <Button
              href={
                armyList?.listUrl ? armyList?.listUrl : `/list/${armyList?.id}`
              }
              target="_blank"
              underline="none"
              variant="outlined"
            >
              {t("viewList")}
            </Button>
            <Button
              href={
                armyList?.event?.id
                  ? `/event/${armyList?.event?.id}?active_tab=placings`
                  : null
              }
              target="_blank"
              underline="none"
              variant="outlined"
            >
              {t("viewEvent")}
            </Button>
          </ButtonGroup>
        </CardActions>
      </Card>
    </Box>
  );
}
